<template>
  <BContainer
    class="mt-2"
    fluid
  >
    <!-- Filters -->
    <BRow>
      <BCol
        cols="12"
        class="px-1"
      >
        <BFormGroup
          label="Search"
          label-for="filterPendingSearch"
          label-class="font-weight-bolder"
        >
          <BFormInput
            id="filterPendingSearch"
            v-model="tablePending.filter.search"
            placeholder="search here"
            autocomplete="off"
            debounce="1000"
            type="text"
          />
        </BFormGroup>
      </BCol>
    </BRow>

    <!-- Table -->
    <BRow
      v-if="Number(tabIndex) === 0"
      class="mt-1"
    >
      <BCol
        cols="12"
        class="px-1"
      >
        <BTable
          ref="tablePending"
          small
          hover
          striped
          bordered
          responsive
          show-empty
          :items="tableProvider"
          :busy="tablePending.busy"
          :filter="tablePending.filter"
          :fields="tablePending.fields"
          :stacked="tablePending.stacked"
          :sort-by.sync="tablePending.sortBy"
          :sort-desc.sync="tablePending.sortDesc"
          :sort-direction="tablePending.sortDirection"
          :filter-included-fields="tablePending.filterOn"
          :current-page="tablePending.currentPage"
          :per-page="tablePending.perPage"
        >
          <template #cell(index)="{ index }">
            {{ tableRowNumber(tablePending, index) }}
          </template>

          <template #row-details="row">
            <div class="p-2 bg-white">
              <BTable
                small
                hover
                bordered
                responsive
                show-empty
                class="col-md-6 col-12"
                :stacked="tablePending.stacked"
                :items="row.item.transfer_details"
                :fields="[
                  { key: 'index', label: '#', class: 'text-center' },
                  { key: 'product_name', label: 'Product Name', class: 'col-md-6 col-12' },
                  { key: 'quantity', label: 'Ordered Quantity', class: 'text-center min-w-100px' },
                  { key: 'approved_quantity', label: 'Approved Quantity', class: 'text-center min-w-100px', formatter: approvedToPostingQuantity }
                ]"
              >
                <template #cell(index)="{ index }">
                  <div style="min-width: 25px;">
                    {{ tableRowNumber(tablePending, index) }}
                  </div>
                </template>

                <template #cell(quantity)="{ value }">
                  <div class="text-md-nowrap d-flex justify-content-center align-items-center min-h-35px">
                    <strong
                      class="font-weight-bolder"
                      v-text="numberFormatter(value, '0,0')"
                    />
                  </div>
                </template>

                <template #cell(approved_quantity)="{ value, item }">
                  <div class="text-md-nowrap d-flex justify-content-center align-items-center min-h-35px">
                    <strong
                      :class="['font-weight-bolder', {
                        'text-danger': Number(value) <= 0,
                        'text-warning': Number(value) >= 1 && Number(item.quantity) !== Number(value),
                        'text-success': Number(item.quantity) === Number(value)
                      }]"
                      v-text="numberFormatter(value, '0,0')"
                    />
                  </div>
                </template>
              </BTable>
            </div>
          </template>

          <template #cell(action)="{ item, toggleDetails, detailsShowing }">
            <div class="text-md-nowrap d-flex flex-column flex-md-row justify-content-md-start justify-content-center gap-1">
              <BButton
                size="sm"
                variant="primary"
                :disabled="tablePending.busy"
                @click.prevent="show(item)"
              >
                View
              </BButton>
              <BButton
                size="sm"
                :variant="detailsShowing ? 'danger' : 'warning' "
                :disabled="tablePending.busy"
                @click.prevent="toggleDetails"
              >
                {{ detailsShowing ? 'Hide' : 'Show' }} Products
              </BButton>
              <BButton
                v-if="Number(item.stage) <= 1"
                size="sm"
                variant="outline-danger"
                :disabled="tablePending.busy"
                @click.prevent="cancelRequest(item)"
              >
                Cancel
              </BButton>
            </div>
          </template>

          <template #cell(transaction_number)="{ value }">
            <b
              class="text-md-nowrap"
              v-text="value"
            />
          </template>

          <template #cell(transfer_number)="{ value }">
            <b
              class="text-md-nowrap"
              v-text="value"
            />
          </template>

          <template #cell(status)="{ value }">
            <b
              :class="[
                'min-w-80px',
                statusByVariant(value, 'text')
              ]"
              v-text="value"
            />
          </template>

          <template #cell(approvals)="{ item, value }">
            <div
              class="text-md-nowrap"
            >
              <div>
                {{ value.approver_label }}
              </div>
              <BProgress
                class="bg-dark-light"
                :value="value.approver_label"
                :max="item.approvals.length"
                show-value
              >
                <BProgressBar :value="value.count">
                  <span><strong>{{ value.count }} / {{ item.approvals.length }}</strong></span>
                </BProgressBar>
              </BProgress>
            </div>
          </template>

          <template #cell()="{ value }">
            <div
              class="text-md-nowrap"
              v-text="value"
            />
          </template>

          <template #table-busy>
            <div class="text-center py-5">
              <BIcon
                icon="stopwatch"
                font-scale="5"
                animation="cylon"
              />
              <p class="h3 py-2">
                <strong>Loading . . .</strong>
              </p>
            </div>
          </template>

        </BTable>
      </BCol>

      <BCol
        cols="12"
        md="6"
        class="mt-1 mb-2 px-1"
      >
        <BFormSelect
          v-model="tablePending.perPage"
          :options="tablePending.pageOptions"
          :disabled="tablePending.busy"
          class="w-100 w-md-25"
          size="sm"
        />
      </BCol>

      <BCol
        cols="12"
        md="6"
        class="d-flex justify-content-center justify-content-md-end mt-1 px-1"
      >
        <BPagination
          v-model="tablePending.currentPage"
          :total-rows="tablePending.totalRows"
          :per-page="tablePending.perPage"
          :disabled="tablePending.busy"
          aria-controls="table"
          prev-text="Prev"
          next-text="Next"
          first-number
          last-number
          pills
        />
      </BCol>

    </BRow>
  </BContainer>
</template>
<script>
import MISC from '@/mixins/misc'
import FORMATTER from '@/mixins/formatter'

import { SWarehousePORequest } from '@/services/user/wo'
// import QUERY from 'lodash'

export default {

  name: 'VWOPORequestPending',

  mixins: [
    MISC,
    FORMATTER
  ],

  props: {
    tabIndex: {
      type: Number,
      default: () => 0
    }
  },

  data () {
    return {
      tablePending: {
        busy: false,
        filter: {
          search: ''
        },
        filterOn: [],
        stacked: this.isMobile(),
        sortBy: 'id',
        sortDesc: false,
        sortDirection: 'asc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-center' },
          { mobile: 12, key: 'action', class: 'text-center' },
          { mobile: 1, key: 'created_at', label: 'Ordered At', class: 'min-w-175px', formatter: this.dateTimeShortFormatter, sortable: true },
          { mobile: 2, key: 'status', label: 'Order Status', class: 'text-center min-w-80px' },
          { mobile: 3, key: 'transaction_number', label: 'Transaction Number', class: 'text-center min-w-150px', sortable: true },
          { mobile: 4, key: 'transfer_number', label: 'Transfer Number', class: 'text-center min-w-150px', sortable: true },
          { mobile: 5, key: 'origin_name', label: 'Ordered To', class: 'min-w-200px' },
          { mobile: 6, key: 'group_name', label: 'Group', class: 'min-w-200px' },
          { mobile: 7, key: 'transfer_details.length', label: 'Products', class: 'text-center' },
          { mobile: 8, key: 'delivery_details.customer', label: 'Customer', class: 'min-w-200px' },
          { mobile: 9, key: 'customer_direct', label: 'D2C', class: 'text-center', formatter: this.yesOrNo },
          { mobile: 10, key: 'approvals', label: 'Current Approver', formatter: this.approvalProgress },
          { mobile: 11, key: 'updated_at', class: 'min-w-175px', formatter: this.dateTimeShortFormatter }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  watch: {
    'stockRequests.selected' (newValues) {
      if (newValues.length === 0) {
        this.selected.indeterminate = false
        this.selected.allSelected = false
      } else if (newValues.length === this.$refs.tablePending?.localItems.length) {
        this.selected.indeterminate = false
        this.selected.allSelected = true
      } else {
        this.selected.indeterminate = true
        this.selected.allSelected = false
      }
    }
  },

  methods: {

    async tableProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      this.tablePending.busy = true
      return await SWarehousePORequest.get({
        page: currentPage,
        per_page: perPage,
        sort: sortBy,
        sort_desc: sortDesc,
        filter_text: filter.search
      }).then(({ data }) => {
        this.tablePending.totalRows = data.total_rows
        return data.items
      }).catch(() => {
        this.tablePending.totalRows = 0
        return []
      }).finally(() => {
        this.tablePending.busy = false
      })
    },

    tableRefresh () {
      this.$refs.tablePending.refresh()
    },

    tableSelectRows (selectAll) {
      this.stockRequests.selected = selectAll ? this.$refs.tablePending.localItems.map(
        item => item.id
      ) : []
    },

    show (stockRequest) {
      this.$emit('show', stockRequest)
    },

    async create (payload) {
      return new Promise((resolve, reject) => {
        SWarehousePORequest.post(payload).then(
          ({ data: { message } }) => {
            this.$bvModal.hide('modalRequest')
            this.swalSuccess(message).then(() => {
              this.tableRefresh()
            })
            resolve(message)
          }
        ).catch(
          ({ message }) => reject(message)
        )
      })
    },

    cancelRequest (stockRequest) {
      this.swalConfirmWithRemarks({
        remarksRequired: true,
        preConfirm: async remarks => await SWarehousePORequest.put({
          id: stockRequest.id,
          remarks: remarks,
          status: 'Canceled'
        }).then(({ data: { message } }) => {
          this.swalSuccess(message).then(() => {
            this.tableRefresh()
          })
        }),
        html: '<h6>Mark this request as Canceled?</h6>',
        confirmButtonText: 'Mark as Canceled',
        cancelButtonText: 'Discard',
        customClass: {
          confirmButton: 'btn btn-sm btn-danger'
        }
      })
    }
  }
}
</script>
